import React, { useEffect, useRef, useState, useCallback } from 'react';
import { decodeVideoFrame } from '@/lib/qr/scanner';
import { DecodedQRResult } from '@/types/qr';
import { Button } from '@/components/ui/Button';
import {
  Camera,
  RefreshCw,
  Flashlight,
  FlashlightOff,
  Play,
  Square
} from 'lucide-react';

interface CameraScannerProps {
  onScanResult: (result: DecodedQRResult) => void;
  isActive: boolean;
}

export const CameraScanner: React.FC<CameraScannerProps> = ({ onScanResult, isActive }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameId = useRef<number | null>(null);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [isTorchOn, setIsTorchOn] = useState(false);
  const [supportsTorch, setSupportsTorch] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const stopCamera = useCallback(() => {
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [stream]);

  const startCamera = useCallback(async () => {
    stopCamera();
    setErrorMessage(null);

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera streaming API is not supported in this browser.');
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play();
      }

      const track = mediaStream.getVideoTracks()[0];
      const capabilities = (track.getCapabilities ? track.getCapabilities() : {}) as any;
      if (capabilities && 'torch' in capabilities) {
        setSupportsTorch(true);
      } else {
        setSupportsTorch(false);
      }
    } catch (err: any) {
      console.warn('Camera access error:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setErrorMessage('Camera access was denied. Please grant camera permission or use the Image Upload scanner.');
      } else {
        setErrorMessage(err.message || 'Unable to access camera.');
      }
    }
  }, [facingMode, stopCamera]);

  const toggleTorch = async () => {
    if (!stream) return;
    const track = stream.getVideoTracks()[0];
    try {
      await (track as any).applyConstraints({
        advanced: [{ torch: !isTorchOn }]
      });
      setIsTorchOn(!isTorchOn);
    } catch (err) {
      console.warn('Torch toggle failed:', err);
    }
  };

  const switchCamera = () => {
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  };

  useEffect(() => {
    if (!isActive || !stream || !videoRef.current || !canvasRef.current) return;

    let isScanning = true;

    const scanFrame = () => {
      if (!isScanning) return;

      if (videoRef.current && canvasRef.current) {
        const result = decodeVideoFrame(videoRef.current, canvasRef.current);
        if (result) {
          if (navigator.vibrate) {
            navigator.vibrate([80]);
          }
          onScanResult(result);
        }
      }

      animationFrameId.current = requestAnimationFrame(scanFrame);
    };

    animationFrameId.current = requestAnimationFrame(scanFrame);

    return () => {
      isScanning = false;
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [isActive, stream, onScanResult]);

  useEffect(() => {
    if (isActive) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [isActive, facingMode]);

  return (
    <div className="relative w-full aspect-square max-w-md mx-auto rounded-2xl overflow-hidden bg-black border border-border flex items-center justify-center">
      <canvas ref={canvasRef} className="hidden" />

      <video
        ref={videoRef}
        playsInline
        muted
        className={`w-full h-full object-cover transition-opacity duration-300 ${
          stream ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {stream && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center p-8">
          <div className="relative w-64 h-64 border border-white/40 rounded-2xl overflow-hidden shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]">
            <div className="absolute top-2 left-2 w-5 h-5 border-t-2 border-l-2 border-white rounded-tl-md" />
            <div className="absolute top-2 right-2 w-5 h-5 border-t-2 border-r-2 border-white rounded-tr-md" />
            <div className="absolute bottom-2 left-2 w-5 h-5 border-b-2 border-l-2 border-white rounded-bl-md" />
            <div className="absolute bottom-2 right-2 w-5 h-5 border-b-2 border-r-2 border-white rounded-br-md" />
            <div className="absolute left-0 right-0 h-0.5 bg-primary animate-[bounce_2.5s_ease-in-out_infinite]" />
          </div>
        </div>
      )}

      {stream && (
        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-center gap-3 z-10">
          <button
            onClick={switchCamera}
            className="p-3 rounded-full bg-black/60 hover:bg-black/80 text-white backdrop-blur-md border border-white/20 transition-transform active:scale-95 cursor-pointer"
            title="Switch Camera"
          >
            <RefreshCw className="h-5 w-5" />
          </button>

          {supportsTorch && (
            <button
              onClick={toggleTorch}
              className={`p-3 rounded-full backdrop-blur-md border transition-transform active:scale-95 cursor-pointer ${
                isTorchOn
                  ? 'bg-warning text-warning-foreground border-warning'
                  : 'bg-black/60 hover:bg-black/80 text-white border-white/20'
              }`}
              title={isTorchOn ? 'Flashlight Off' : 'Flashlight On'}
            >
              {isTorchOn ? <FlashlightOff className="h-5 w-5" /> : <Flashlight className="h-5 w-5" />}
            </button>
          )}

          <button
            onClick={stopCamera}
            className="p-3 rounded-full bg-destructive hover:bg-destructive/90 text-destructive-foreground backdrop-blur-md border border-transparent transition-transform active:scale-95 cursor-pointer"
            title="Pause Camera"
          >
            <Square className="h-5 w-5" />
          </button>
        </div>
      )}

      {!stream && (
        <div className="absolute inset-0 p-6 flex flex-col items-center justify-center text-center space-y-4 bg-[#1d1d1f]">
          <div className="p-4 rounded-full bg-white/10 text-white">
            <Camera className="h-10 w-10" />
          </div>
          <div>
            <h4 className="text-[21px] font-semibold text-white tracking-tight">Camera Scanner</h4>
            <p className="text-[14px] text-[#a1a1a6] mt-1 max-w-xs leading-relaxed">
              {errorMessage || 'Point your camera at any standard QR code.'}
            </p>
          </div>
          <Button
            variant="primary"
            size="md"
            onClick={startCamera}
            leftIcon={<Play className="h-4 w-4" />}
          >
            Start Camera
          </Button>
        </div>
      )}
    </div>
  );
};
